import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RecipientPickerCard, PickerContact } from "./RecipientPickerCard";
import { CampaignProgressCard } from "./CampaignProgressCard";
import {
  cancelOutreachCampaign,
  getOutreachCampaignStatus,
  renderMergeFields,
  searchOutreachRecipients,
  startOutreachCampaign,
} from "../../../../features/ghl/services/ghlOutreach";
import type { GhlContact, GhlSearchCursor } from "../../../../services/contactImportService";
import type { CampaignStatusAttachment, OutreachEmailDraft } from "../types";
import { debug } from "../../../../utils/debugLogger";

interface OutreachCampaignFlowProps {
  draft: OutreachEmailDraft;
  customerId: string;
  idToken: string;
  /** Announces the new campaign in the thread.
   *
   *  The send happens outside the conversation, so without this the agent never
   *  learns the campaign id and cannot answer "who replied?" — its reply and
   *  status tools both take one. Posting the id once puts it in history where
   *  the model can pick it up. */
  onCampaignStarted?: (campaignId: string, total: number) => void;
}

const PAGE_SIZE = 100;
const POLL_INTERVAL_MS = 5000;

const toPickerContact = (c: GhlContact): PickerContact => ({
  id: c.ghl_contact_id,
  name: c.name,
  email: c.email,
  company: c.company,
  tags: c.tags,
});

/** Say what actually went wrong.
 *
 *  A bare "could not start the campaign" is useless to debug against: the local
 *  agent shim does not verify Cognito, so an expired session still lets the
 *  recipient picker load and only fails here, against the real API — which
 *  looks like a broken send rather than a stale login. The status and the
 *  server's own message are worth more than a tidy sentence. */
const errorText = (error: unknown, fallback: string): string => {
  // A bare "Network Error" means axios never got a response — CORS, a dropped
  // connection, a blocking extension. The status line alone cannot tell those
  // apart, so put the whole thing in the console for whoever has to work it out.
  debug.error("outreach request failed", error);
  const err = error as {
    response?: { status?: number; data?: { error?: string; message?: string } };
    message?: string;
  };
  const status = err?.response?.status;
  const serverMessage = err?.response?.data?.error || err?.response?.data?.message;

  if (status === 401) return "Your session has expired — sign in again and retry.";
  if (status === 403) return `Not allowed for this customer${serverMessage ? `: ${serverMessage}` : ""}.`;
  if (status === 404) return "This business is not connected to GoHighLevel yet.";
  if (status === 409) return "The GoHighLevel connection needs to be reauthorized.";
  if (status) return `${fallback} (HTTP ${status}${serverMessage ? ` — ${serverMessage}` : ""})`;
  // No response at all: blocked by CORS, offline, or the request never left.
  return `${fallback} (${err?.message || "no response from the server"})`;
};

/**
 * Everything that happens after the outreach draft is approved: pick recipients,
 * send, watch progress.
 *
 * The send deliberately does not go through the chat model. Routing it through
 * the agent meant the selected contact IDs travelled as a sentence, which the
 * model re-read as a search term and answered by re-drafting the email — losing
 * the approved copy and sending nothing. Selection and send are plain UI state
 * and a direct API call; the model's outreach tools remain for the purely
 * conversational path ("email john@example.com about the meetup").
 */
export const OutreachCampaignFlow: React.FC<OutreachCampaignFlowProps> = ({
  draft,
  customerId,
  idToken,
  onCampaignStarted,
}) => {
  const [contacts, setContacts] = useState<PickerContact[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<GhlSearchCursor | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  /** The term the loaded rows came back for. Typing changes `search`; only
   *  pressing Search changes this, so the rows on screen always match it. */
  const [appliedQuery, setAppliedQuery] = useState("");
  /** Bumped on every submit so pressing Search re-runs even for the same term. */
  const [searchNonce, setSearchNonce] = useState(0);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [confirming, setConfirming] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState<CampaignStatusAttachment | null>(null);

  const canRun = Boolean(draft.approved && customerId && idToken);

  // Guards against a slow earlier search overwriting a newer one's results.
  const searchSeq = useRef(0);

  const loadPage = useCallback(
    async (query: string, append: boolean, pageCursor?: GhlSearchCursor | null) => {
      const seq = searchSeq.current + 1;
      searchSeq.current = seq;
      if (append) setIsLoadingMore(true);
      else setIsSearching(true);
      try {
        const page = await searchOutreachRecipients(customerId, idToken, {
          query,
          limit: PAGE_SIZE,
          cursor: pageCursor ?? undefined,
        });
        if (seq !== searchSeq.current) return;
        const mapped = page.contacts.map(toPickerContact);
        setContacts((prev) => (append ? [...prev, ...mapped] : mapped));
        setTotal(page.total);
        setCursor(page.nextCursor);
        setError(null);
      } catch (e) {
        if (seq !== searchSeq.current) return;
        setError(errorText(e, "Could not load contacts."));
      } finally {
        if (seq === searchSeq.current) {
          setIsSearching(false);
          setIsLoadingMore(false);
        }
      }
    },
    [customerId, idToken],
  );

  // Approving the draft is what opens the picker — no chat round-trip, no
  // waiting on the model to decide to list contacts. Re-runs on each submitted
  // search rather than per keystroke: this is a live GHL call, and the picker
  // should not fire one for every letter of a name.
  useEffect(() => {
    if (!canRun || status) return;
    void loadPage(appliedQuery, false, null);
  }, [canRun, appliedQuery, searchNonce, loadPage, status]);

  const submitSearch = useCallback(() => {
    setAppliedQuery(search.trim());
    setSearchNonce((n) => n + 1);
  }, [search]);

  // Poll while the campaign is live. Recipients are sent one at a time on a
  // server-side pace, so the card would otherwise sit on its first reading.
  //
  // Failures used to be swallowed entirely, on the theory that the next tick
  // would succeed. When they didn't, the card sat on its seeded "0 sent /
  // sending" forever while the campaign had in fact completed — indisputably
  // worse than saying so. One bad tick is still ignored; a run of them is not.
  const pollFailures = useRef(0);

  // Depend on the id and liveness, never on the status object: each poll
  // replaces it, so an object dependency would tear down and re-run this effect
  // on every tick — and with an immediate read at the top, that is a request
  // storm rather than a poll.
  const pollingCampaignId = status?.campaign_id ?? null;
  const campaignIsLive = status?.status === "sending" || status?.status === "pending";

  useEffect(() => {
    if (!pollingCampaignId || !campaignIsLive) return;
    const campaignId = pollingCampaignId;
    let cancelled = false;
    const poll = async () => {
      try {
        const next = await getOutreachCampaignStatus(customerId, idToken, campaignId);
        if (cancelled) return;
        setStatus(next);
        pollFailures.current = 0;
        setError(null);
      } catch (e) {
        if (cancelled) return;
        pollFailures.current += 1;
        if (pollFailures.current >= 3) {
          setError(errorText(e, "Lost track of this campaign — it is still sending."));
        }
      }
    };

    // Read once immediately. The seeded card is a guess made before the server
    // had said anything, so waiting a full interval to correct it is a needless
    // five seconds of showing numbers that may already be wrong.
    void poll();
    const handle = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [pollingCampaignId, campaignIsLive, customerId, idToken]);

  // Selections survive a new search, which replaces the loaded rows — so the
  // picked contacts are remembered here rather than looked up in `contacts`,
  // where someone chosen under an earlier search no longer appears.
  const [selectedDetails, setSelectedDetails] = useState<Map<string, PickerContact>>(new Map());

  const contactsRef = useRef<PickerContact[]>([]);
  contactsRef.current = contacts;

  const remember = useCallback((ids: string[], select: boolean) => {
    setSelectedDetails((prev) => {
      const next = new Map(prev);
      ids.forEach((id) => {
        if (!select) {
          next.delete(id);
          return;
        }
        const found = contactsRef.current.find((c) => c.id === id);
        if (found) next.set(id, found);
      });
      return next;
    });
  }, []);

  const toggle = useCallback(
    (id: string) => {
      const willSelect = !selectedIds.has(id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (willSelect) next.add(id);
        else next.delete(id);
        return next;
      });
      remember([id], willSelect);
    },
    [remember, selectedIds],
  );

  const toggleVisible = useCallback(
    (ids: string[], select: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => (select ? next.add(id) : next.delete(id)));
        return next;
      });
      remember(ids, select);
    },
    [remember],
  );

  const selectedContacts = useMemo(
    () => Array.from(selectedIds).map((id) => selectedDetails.get(id)).filter(Boolean) as PickerContact[],
    [selectedIds, selectedDetails],
  );

  // Which recipient the confirmation is previewing. Every recipient gets their
  // own rendering of the same template, so one preview only proves one of them
  // reads correctly — the point of this is catching a merge field that comes
  // out blank or wrong for somebody specific.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const previewContact =
    selectedContacts.find((c) => c.id === previewId) ?? selectedContacts[0];

  const previewFor = useCallback(
    (template: string) =>
      renderMergeFields(
        template,
        previewContact
          ? {
              ghl_contact_id: previewContact.id,
              name: previewContact.name,
              company: previewContact.company || "",
              email: previewContact.email,
              phone: "",
              tags: previewContact.tags || [],
            }
          : undefined,
      ),
    [previewContact],
  );
  const previewBody = useMemo(() => previewFor(draft.body), [previewFor, draft.body]);
  const previewSubject = useMemo(() => previewFor(draft.subject), [previewFor, draft.subject]);

  const send = useCallback(async () => {
    if (isSending || selectedIds.size === 0) return;
    setIsSending(true);
    setError(null);
    try {
      const result = await startOutreachCampaign(customerId, idToken, {
        subject: draft.subject,
        bodyTemplate: draft.body,
        contactIds: Array.from(selectedIds),
      });
      setConfirming(false);
      setStatus({
        campaign_id: result.campaign_id,
        status: "sending",
        sent: 0,
        total: result.total,
        failed: 0,
        skipped: 0,
        recipients: [],
      });
      onCampaignStarted?.(result.campaign_id, result.total);
    } catch (e) {
      const err = e as { response?: { status?: number; data?: { campaign_id?: string } } };
      if (err?.response?.status === 409) {
        // The idempotency lock: this exact draft and recipient list was already
        // submitted. Show that campaign rather than starting a second one — 409
        // here means "duplicate", not the needs_reauth it means on the GHL
        // token routes, so it must not fall through to the shared mapping.
        const existing = err.response.data?.campaign_id;
        if (!existing) {
          setError("This campaign was already started — reload the thread to see its progress.");
          return;
        }
        setConfirming(false);
        setStatus({
          campaign_id: existing,
          status: "sending",
          sent: 0,
          total: selectedIds.size,
          failed: 0,
          skipped: 0,
          recipients: [],
        });
      } else {
        setError(errorText(e, "Could not start the campaign."));
      }
    } finally {
      setIsSending(false);
    }
  }, [customerId, idToken, draft.subject, draft.body, selectedIds, isSending, onCampaignStarted]);

  const cancel = useCallback(
    async (campaignId: string) => {
      try {
        await cancelOutreachCampaign(customerId, idToken, campaignId);
        setStatus((prev) => (prev ? { ...prev, status: "cancelled" } : prev));
      } catch (e) {
        setError(errorText(e, "Could not cancel the campaign."));
      }
    },
    [customerId, idToken],
  );

  if (!canRun) return null;

  if (status) {
    return (
      <>
        <CampaignProgressCard data={status} onCancel={cancel} />
        {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      </>
    );
  }

  if (confirming) {
    const withoutEmail = selectedContacts.filter((c) => !c.email).length;
    return (
      <div className="my-3 w-full min-w-0 max-w-full rounded-2xl border border-theme-border bg-theme-surface-ui p-4">
        <p className="text-sm font-semibold text-theme-fg mb-1">
          Send to {selectedIds.size} {selectedIds.size === 1 ? "recipient" : "recipients"}?
        </p>
        <p className="text-xs text-theme-fg-muted mb-3">
          Emails go out one at a time, roughly every 30 seconds. Sent email cannot be recalled.
          {withoutEmail > 0 && ` ${withoutEmail} selected contact(s) have no email and will be skipped.`}
        </p>

        {/* One chip per recipient — click to see the copy that person receives. */}
        <p className="text-[11px] uppercase tracking-wide text-theme-fg-muted mb-1">
          Recipients — click one to preview their email
        </p>
        <div className="mb-3 flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
          {selectedContacts.map((contact) => {
            const isActive = previewContact?.id === contact.id;
            const noEmail = !contact.email;
            return (
              <button
                key={contact.id}
                type="button"
                onClick={() => setPreviewId(contact.id)}
                disabled={isSending}
                title={contact.email || "No email — will be skipped"}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors disabled:opacity-60 ${
                  isActive
                    ? "bg-theme-primary text-white"
                    : "bg-theme-secondary/15 text-theme-fg hover:bg-theme-secondary/25"
                } ${noEmail ? "line-through opacity-60" : ""}`}
              >
                {contact.name || contact.email || contact.id}
              </button>
            );
          })}
        </div>

        <p className="text-[11px] uppercase tracking-wide text-theme-fg-muted mb-1">
          Preview{previewContact ? ` — as ${previewContact.name || previewContact.email}` : ""}
        </p>
        <div className="rounded-xl border border-theme-border bg-theme-surface p-3 mb-3">
          <p className="text-sm font-medium text-theme-fg mb-1">{previewSubject}</p>
          <p className="whitespace-pre-wrap text-xs leading-relaxed text-theme-fg-muted">{previewBody}</p>
        </div>

        {error && <p className="mb-2 text-xs text-red-600 dark:text-red-400">{error}</p>}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={isSending}
            className="flex-1 rounded-xl border border-theme-border py-2 text-sm font-medium text-theme-fg transition-colors hover:bg-theme-secondary/15 disabled:opacity-60"
          >
            Back
          </button>
          <button
            type="button"
            onClick={send}
            disabled={isSending}
            className="flex-1 rounded-xl bg-theme-primary py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            {isSending ? "Starting..." : "Send now"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <RecipientPickerCard
      contacts={contacts}
      total={total}
      selectedIds={selectedIds}
      onToggle={toggle}
      onToggleVisible={toggleVisible}
      search={search}
      onSearchChange={setSearch}
      onSearchSubmit={submitSearch}
      appliedQuery={appliedQuery}
      activeTag={activeTag}
      onTagChange={setActiveTag}
      isSearching={isSearching}
      hasMore={Boolean(cursor)}
      isLoadingMore={isLoadingMore}
      onLoadMore={() => void loadPage(appliedQuery, true, cursor)}
      onSend={() => setConfirming(true)}
      isSending={isSending}
      error={error}
    />
  );
};
